import { useQuery } from "@tanstack/react-query";
import { useWatch } from "react-hook-form";
import { useEffect, useState } from "react";

import { useArtworkEnhancedImages, useArtwork, useArtworkForm } from "@design-lab/entities/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import type { printOnDemand } from "common/utils/models";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { generatePreview } from "./generatePreview";

type ArtworkPreviewProps = {
  composition: printOnDemand.getProduct.Composition;
  onClick: () => void;
};

type ArtworkSidePreviewProps = {
  composition: printOnDemand.getProduct.Composition;
  placement: PlacementFormPosition;
};

function useArtworkSidePreview(props: ArtworkSidePreviewProps) {
  const { composition, placement } = props;
  const { form } = useArtworkForm();
  const { getArtwork } = useArtwork();
  const artworkForm = useWatch({ control: form.control });
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const selectedPlacement = artworkForm?.[placement];
  const artwork = selectedPlacement?.artworkId ? getArtwork(selectedPlacement.artworkId) : null;
  const info = artworkForm?.info?.[placement === "back" ? "back" : "front"];
  const enhancedImages = useArtworkEnhancedImages(
    artwork || undefined,
    info?.removeBackground ?? false,
    artwork?.canRemoveBg ?? true,
    info?.editImageManual ?? false
  );
  const image = info?.enhancementTier ? enhancedImages?.[info.enhancementTier]?.base64 : undefined;
  const surface = composition.surfaces[placement];

  const { data: preview } = useQuery({
    enabled: Boolean(image && selectedPlacement?.size),
    queryKey: [
      "preview",
      composition.id,
      placement,
      image,
      selectedPlacement?.size
        ? `${selectedPlacement.size.width}x${selectedPlacement.size.height}`
        : undefined
    ],
    queryFn: () =>
      image && selectedPlacement?.size?.width && selectedPlacement?.size?.height
        ? generatePreview({
            imageUrl: image,
            artworkSize: {
              width: selectedPlacement.size.width,
              height: selectedPlacement.size.height
            },
            surface
          })
        : undefined
  });

  // Manage Object URL lifecycle to prevent memory leaks
  useEffect(() => {
    if (preview?.blob) {
      const url = URL.createObjectURL(preview.blob);
      setObjectUrl(url);

      // Cleanup function to revoke the object URL
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setObjectUrl(null);
    return undefined;
  }, [preview?.blob]);

  if (!preview?.blob || !objectUrl) return null;

  return <img src={objectUrl} alt="Preview" className="size-full object-contain" />;
}

export function ArtworkPreview(props: ArtworkPreviewProps) {
  const { composition, onClick } = props;
  const { form } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const artworkForm = useWatch({ control: form.control });
  const frontPreview = useArtworkSidePreview({
    composition,
    placement: artworkForm?.selectedFrontPlacement || "front"
  });
  const backPreview = useArtworkSidePreview({
    composition,
    placement: "back"
  });

  const currentCompositionId = useWatch({
    control: compositionForm.control,
    name: "currentCompositionId"
  });

  const preview = frontPreview || backPreview || (
    <img src={composition.imageUrl} alt="Preview" className="size-full object-contain" />
  );

  return (
    <div
      className={cn(
        "size-20 rounded-md p-1 transition-all duration-300 hover:border hover:border-gray-800 hover:shadow-lg lg:size-32",
        currentCompositionId === composition.id && "border border-gray-800 shadow-lg"
      )}
    >
      <button
        data-testid="artwork-preview-button"
        className="bg-background relative size-full cursor-pointer overflow-hidden rounded border border-gray-200 hover:border-gray-300"
        onClick={() => onClick()}
      >
        {preview}
        {frontPreview && backPreview && (
          <div className="bg-background lg:w-15 lg:h-15 absolute bottom-[-1px] right-[-1px] h-10 w-10 rounded border border-gray-200">
            {backPreview}
          </div>
        )}
      </button>
    </div>
  );
}
